import type { PrescreenFields } from './pages/prescreen-form'
import type { ContactAgentMessage, ContactFormValues } from './types'
import type { UserProfile } from 'store/users/types'

import { useMediaQuery, useTheme } from '@mui/material'
import CircularProgress from '@rentspree/component-2023.components.organisms.circular-progress'
import Drawer from '@rentspree/component-2023.components.organisms.drawer'
import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useTurnstile } from 'react-turnstile'

import env from 'env'
import { useDispatch, useSelector } from 'store'
import { selectAgentProfile } from 'store/agent-profile'
import { showAlertDialog } from 'store/alert-dialog'
import { selectPublicListingDataToRCM } from 'store/public-listing'
import { selectPublicMessagingApiStatus } from 'store/public-messaging'
import { asyncActions } from 'store/public-messaging/async-actions'
import { selectUserProfile, selectUserProfileUserType } from 'store/users'
import tracker from 'tracker'
import { LOCATION, MAIN_FEATURE } from 'tracker/events'
import { interpolateMessage } from 'utils/interpolate-message'

import { DEFAULT_MESSAGE_TEMPLATE, DRAWER_PAGE, MESSAGE_TYPE } from './constants'
import { ContactForm } from './pages/contact-form'
import { PrescreenForm } from './pages/prescreen-form'
import { parsePrescreenFormData } from './utils'
import { contactFormValidation, prescreenFormValidation } from './validations'

interface Props {
  isOpen: boolean
  onModalClose: () => void
  contactDetails: ContactDetails
  profile?: UserProfile
}

export const ContactDrawer: React.FC<Props> = (props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { onModalClose, isOpen, contactDetails } = props
  const dispatch = useDispatch()
  const [emailApiRequestSent, setEmailApiRequestSent] = useState(false)
  const { id: renterId, fullName } = useSelector(selectUserProfile)
  const [currentPage, setCurrentPage] = useState(DRAWER_PAGE.CONTACT_FORM)
  const isContactFormPage = currentPage === DRAWER_PAGE.CONTACT_FORM
  const { userId, email: agentEmail } = useSelector(selectAgentProfile)
  const publicListingDataToRCM = useSelector(selectPublicListingDataToRCM)
  const sendEmailApiStatus = useSelector(selectPublicMessagingApiStatus)
  const userType = useSelector(selectUserProfileUserType)
  const isTurnstileEnabled = env?.TURNSTILE_ENABLED
  const turnstile = useTurnstile()
  const defaultMessage = interpolateMessage(DEFAULT_MESSAGE_TEMPLATE, {
    agent_name: contactDetails.firstName,
    rental_property_address: publicListingDataToRCM.address,
  })

  const [isTurnstileLoading, setIsTurnstileLoading] = useState(false)

  const formik = useFormik<ContactFormValues>({
    initialValues: {
      message: defaultMessage,
      moveInDate: '',
      preferredLeaseTerm: '',
      householdMonthlyIncome: '',
      occupy: '1',
    },
    onSubmit: (values) => {
      const prescreenFields: PrescreenFields = {
        moveInDate: values.moveInDate,
        preferredLeaseTerm: values.preferredLeaseTerm,
        householdMonthlyIncome: values.householdMonthlyIncome,
        occupy: values.occupy,
      }
      const prescreenMessageData = parsePrescreenFormData(prescreenFields)
      const { message } = values

      const messages: ContactAgentMessage[] = [
        { data: message || defaultMessage, type: MESSAGE_TYPE.MESSAGE },
      ]
      messages.push({
        data: { ...prescreenMessageData, propertyId: publicListingDataToRCM.id },
        type: MESSAGE_TYPE.PRE_SCREEN,
      })
      const turnstileToken = isTurnstileEnabled ? turnstile.getResponse() : ''

      if (userId && agentEmail) {
        const payload = {
          sender: {
            fullName: fullName || '',
          },
          recipient: {
            _id: userId,
            email: agentEmail,
          },
          messages,
          property: publicListingDataToRCM,
        }
        void dispatch(
          asyncActions.sendContactWithPrescreenToAgent({
            ...payload,
            source: 'listing-syndication',
            turnstile,
            turnstileToken,
          }),
        )
        setEmailApiRequestSent(true)
      }
    },
    validationSchema: isContactFormPage ? contactFormValidation : prescreenFormValidation,
  })
  const isLoading = sendEmailApiStatus === 'pending'

  useEffect(() => {
    if (isOpen) {
      formik.resetForm()
      setCurrentPage(DRAWER_PAGE.CONTACT_FORM)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (emailApiRequestSent) {
      switch (sendEmailApiStatus) {
        case 'fulfilled':
          onModalClose()
          dispatch(
            showAlertDialog({
              title: 'Message Sent',
              message: 'The listing agent for this property will reach out to your soon.',
              type: 'success',
            }),
          )
          break
        case 'rejected':
          onModalClose()
          dispatch(
            showAlertDialog({
              title: 'Failed to send',
              message: 'Something went wrong while sending message. Please try again.',
              type: 'error',
            }),
          )
          break
        default:
          break
      }
    }
  }, [dispatch, emailApiRequestSent, onModalClose, renterId, sendEmailApiStatus, userType])

  const handleSubmit = async (): Promise<void> => {
    const errors = await formik.validateForm()
    const hasErrors = Object.keys(errors).length > 0

    if (hasErrors) {
      const touchedFields = Object.keys(errors).reduce<Record<string, boolean>>(
        (acc, key) => ({
          ...acc,
          [key]: true,
        }),
        {},
      )
      void formik.setTouched(touchedFields)
      return
    }

    if (isContactFormPage) {
      tracker?.trackButtonClick({
        click_text: 'Continue',
        location: LOCATION.CONTACT_HOST_MODAL,
        screen_experience: isMobile ? 'mobile' : 'desktop',
        main_feature: MAIN_FEATURE.MARKET_LISTING,
      })
      setCurrentPage(DRAWER_PAGE.PRESCREEN_FORM)
    } else {
      tracker?.trackButtonClick({
        click_text: 'Send message',
        location: LOCATION.CONTACT_HOST_MODAL,
        screen_experience: isMobile ? 'mobile' : 'desktop',
        main_feature: MAIN_FEATURE.MARKET_LISTING,
      })
      formik.handleSubmit()
    }
  }

  const handleCloseModal = () => {
    tracker?.trackButtonClick({
      click_text: 'Close',
      location: LOCATION.CONTACT_HOST_MODAL,
      screen_experience: isMobile ? 'mobile' : 'desktop',
      main_feature: MAIN_FEATURE.MARKET_LISTING,
    })
    onModalClose()
  }

  const contactFormPageActionsProps = {
    primaryButtonText: 'Continue',
    primaryButtonProps: {
      onClick: handleSubmit,
    },
  }

  const preScreenFormPageActionsProps = {
    primaryButtonText: 'Send message',
    primaryButtonProps: {
      onClick: handleSubmit,
      disabled: isLoading || isTurnstileLoading,
      startIcon: isLoading && <CircularProgress sx={{ color: 'grey !important' }} size="icon" />,
      color: 'primary',
    },
    secondaryButtonText: 'Back',
    secondaryButtonProps: {
      wrapped: true,
      onClick: () => {
        tracker?.trackButtonClick({
          click_text: 'Back',
          location: LOCATION.CONTACT_HOST_MODAL,
          screen_experience: isMobile ? 'mobile' : 'desktop',
          main_feature: MAIN_FEATURE.MARKET_LISTING,
        })
        setCurrentPage(DRAWER_PAGE.CONTACT_FORM)
      },
      disabled: isLoading,
    },
  }

  const actionButtonsProps =
    currentPage === DRAWER_PAGE.CONTACT_FORM
      ? contactFormPageActionsProps
      : preScreenFormPageActionsProps

  const subHeader =
    currentPage === DRAWER_PAGE.CONTACT_FORM ? 'Contact Agent' : 'Add a few more details'

  return (
    <Drawer
      className="visibility-tracking element_visibility"
      data-event="element_visibility"
      data-location={LOCATION.CONTACT_HOST_MODAL}
      data-main_feature={MAIN_FEATURE.MARKET_LISTING}
      data-screen_experience={isMobile ? 'mobile' : 'desktop'}
      open={isOpen}
      onClose={handleCloseModal}
      size="small"
      anchor="right"
      displayCloseButton
      subHeader={subHeader} // TODO: fix subheader style
      actionButtonsProps={actionButtonsProps}
    >
      <form>
        {currentPage === DRAWER_PAGE.CONTACT_FORM ? (
          <ContactForm formik={formik} contactDetails={contactDetails} />
        ) : (
          <PrescreenForm formik={formik} setIsTurnstileLoading={setIsTurnstileLoading} />
        )}
      </form>
    </Drawer>
  )
}
